import get from "lodash/get";
import React, { useMemo } from "react";
import { Button, Modal } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import AccountingReportFilesForm from "~/components/Forms/AccountingReportFilesForm";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  GetFileDealerReportRepType,
  useGetAccountingFileDealerReportLazyQuery,
  useGetAccountingsGettersQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { Select } from "~/shared/ui/Select";

import AccountingTable from "./AccountingTable";
import AccountingExcelReportModal, {
  ExcelFormData,
} from "./modals/AccountingExcelReportModal";
import { APPENDIX, APPENDIX_2 } from "./options";

import s from "./AccountingPeriod.less";

const FORM_OPTIONS = ["form1", "form2"];

const AccountingPeriod = () => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();
  const { dealerChoice } = useUser();

  const { params } = useRouteMatch();
  const history = useHistory();

  const { searchField, sortType, openModal, searchTextForSelect } =
    useGetLocationData();

  const goBackHandler = () => {
    const updatedUrl = `/accounting/${
      dealerChoice === "one" ? "" : params["period"]
    }?searchTextForSelect=${searchTextForSelect || ""}`;
    history.replace(updatedUrl);
  };

  const formValue = sortType || "form1";
  const isForm = formValue === "form1";
  const appendixOptions = isForm ? APPENDIX : APPENDIX_2;
  const appendixValue = searchField || appendixOptions[0];

  const { notifyReport } = useNotify();
  const [
    GetAccountingFileDealerReportLazyQuery,
    AccountingFileDealerReportLazyQuery,
  ] = useGetAccountingFileDealerReportLazyQuery({
    onCompleted(data) {
      const hash = get(data, "AccountingGetter.getFileDealerReport.fileHash");

      hash && notifyReport(hash);
    },
    fetchPolicy: "no-cache",
  });

  const { data, refetch, loading } = useGetAccountingsGettersQuery({
    variables: {
      DealerId: params["id"],
      ReportPeriodId: params["period"],
    },
  });

  const accountingsData = get(
    data,
    "AccountingGetter.getDealerReports.items",
    [],
  );

  const result = useMemo(
    () =>
      [...accountingsData].sort((a, b) =>
        a.ReportPeriod.Year !== b.ReportPeriod.Year
          ? b.ReportPeriod.Year - a.ReportPeriod.Year
          : b.ReportPeriod.Quarter - a.ReportPeriod.Quarter,
      ),
    [accountingsData],
  );

  const filesLinks =
    result && result[0]?.Files.length > 0 && result[0]["Files"];

  const status = result && result[0]?.status;

  const onSubmit = () => {
    setDataReplace("openModal", "Excel");
  };
  const onFilesSumbit = () => {
    setDataReplace("openModal", "Files", "currentId", result[0]["id"]);
  };

  const onReport = (data: ExcelFormData) => {
    GetAccountingFileDealerReportLazyQuery({
      variables: {
        DealerId: params["id"],
        repType:
          GetFileDealerReportRepType[
            data.report as keyof typeof GetFileDealerReportRepType
          ],
        firstPartPeriodId:
          data.report !== "Second" ? params["period"] : undefined,
        secondPartPeriodId:
          data.report !== "First" ? params["period"] : undefined,
      },
    });
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "currentId", "");
  };

  const setSearchField = (value: string) => {
    setData("searchField", value);
  };

  const setSearchForm = (value: string) => {
    setData(
      "sortType",
      value,
      "searchField",
      value === "form1" ? "appendix_1" : "appendix_6",
    );
  };

  return (
    <Wrapper.Content
      title={`${t("accounting.reportDealer")} ${
        accountingsData.length > 0 ? accountingsData[0]?.Dealer?.DealerName : ""
      }`}
      txtBtn={t("ui.back")}
      goBackHandler={goBackHandler}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("accounting.getFileDealerReport") && (
            <Button
              size={"middle" as any}
              color="success"
              loading={AccountingFileDealerReportLazyQuery.loading}
              onClick={onSubmit}
            >
              {t("accounting.accountingReport")}
            </Button>
          )}
          {(hasPermission("accounting.setDealerReport.check") ||
            hasPermission("accounting.setDealerReport.confirm")) && (
            <Button size={"middle" as any} onClick={onFilesSumbit}>
              {t("accounting.planReportFiles")}
            </Button>
          )}
        </Flex>
      }
      filterComponent={
        <Flex gap={10} className={s.filter}>
          <Select
            value={formValue}
            options={FORM_OPTIONS.map((value) => ({
              value,
              label: t(`accounting.${value}`),
            }))}
            onChange={setSearchForm}
            className={s.selectWidth}
          />
          <Select
            value={appendixValue}
            options={appendixOptions.map((value) => ({
              value,
              label: t(`accounting.${value}`),
            }))}
            onChange={setSearchField}
            className={s.selectWidth}
          />
        </Flex>
      }
    >
      <div className={s.text}>
        {!loading && (
          <div className={s.statusClass}>
            {t("accounting.status")}: {t(`accounting.${result[0]["status"]}`)}
          </div>
        )}
      </div>
      {!loading && !!result[0]["note"] && (
        <div className={s.txtBts}>
          {t("accounting.fixingReportNote")}: {result[0]["note"]}
        </div>
      )}
      <AccountingTable
        loading={loading}
        accountingsData={accountingsData}
        refetch={refetch}
      />
      <AccountingExcelReportModal
        loading={AccountingFileDealerReportLazyQuery.loading}
        onReport={onReport}
      />
      <Modal
        open={openModal === "Files"}
        title={t("accounting.planReportFiles")}
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
        footer={null}
      >
        <AccountingReportFilesForm
          setCloseModal={handleCloseModal}
          Files={filesLinks}
          status={status}
          refetch={refetch}
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default AccountingPeriod;
